/**
 * Microscope-objective magnification picker.
 *
 * Commercial-grade, VaultPod-native design — NOT a copy of any other
 * product. Each preset is a stylised pathology objective lens drawn
 * with SVG: a truncated cone (metal barrel), a colour ring following
 * the classic RMS convention (red / yellow / green / blue / ivory),
 * a collar, and the front lens glint. Below the barrel sits the
 * magnification string (`5×`, `10×`, ..., `100×ᴰ` for digital).
 *
 * Layout:
 *   desktop  → horizontal row centred at the top of the viewport
 *   mobile   → a single "selector" pill that opens a dropdown
 *              popover (same 5 objectives, stacked vertically)
 *
 * The picker lives as an absolutely-positioned overlay ABOVE the
 * canvas so it survives the fullscreen toggle. It replaces the
 * previous text-chip row that used to live inside the (hideable)
 * top bar.
 */
import { createSignal, For, Show } from "solid-js";

export interface MagObjective {
  /** Objective-equivalent magnification (5 / 10 / 20 / 40 / 100). */
  x: number;
  /** Ring colour following the classic Royal Microscopical Society
   *  code as closely as each of our five stops allows — useful
   *  muscle memory for any pathologist who's stood in front of a
   *  real scope. */
  ring: string;
  /** Optional darker rim under the ring for a sharper read. */
  rim?: string;
}

// VaultPod objective palette. Hex values chosen to pop on both the
// dark (#0d1117) viewport and light tissue under strong stains.
export const PATHOLOGY_OBJECTIVES: MagObjective[] = [
  { x: 5,   ring: "#DC2626", rim: "#7F1D1D" },  // red     — scanning power
  { x: 10,  ring: "#EAB308", rim: "#854D0E" },  // yellow  — low power
  { x: 20,  ring: "#22C55E", rim: "#14532D" },  // green   — medium
  { x: 40,  ring: "#3B82F6", rim: "#1E3A8A" },  // blue    — high dry
  { x: 100, ring: "#F8FAFC", rim: "#64748B" },  // ivory   — oil immersion
];

export interface MagObjectivePickerProps {
  /** Current objective-equivalent magnification. */
  current: number;
  /** True when the current zoom exceeds the finest native level —
   *  used to italicise / mark the "ᴰ" digital-zoom flag. */
  digital: boolean;
  /** Pathologist's native mag at zoom=1 (derived from base MPP). */
  nativeMag?: number;
  /** Fired when the user picks a different objective. */
  onPick: (x: number) => void;
}

/** One stylised objective lens. The body is a vertical truncated
 *  cone (metal barrel) with a coloured ring, a collar, and a subtle
 *  glint to feel tactile. All-SVG so it scales cleanly on every DPR
 *  and doesn't need an asset pipeline. */
function ObjectiveIcon(p: { obj: MagObjective; active: boolean; size?: number }) {
  const W = p.size ?? 28;
  const H = Math.round(W * 44 / 28);
  return (
    <svg viewBox="0 0 28 44" width={W} height={H} aria-hidden="true"
         class="drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
      <defs>
        <linearGradient id={`barrel-${p.obj.x}`} x1="0" y1="0" x2="28" y2="0">
          <stop offset="0"   stop-color="#1F2937" />
          <stop offset="0.45" stop-color="#CBD5E1" />
          <stop offset="0.6"  stop-color="#F8FAFC" />
          <stop offset="1"   stop-color="#1F2937" />
        </linearGradient>
        <linearGradient id={`ring-${p.obj.x}`} x1="0" y1="0" x2="28" y2="0">
          <stop offset="0"  stop-color={p.obj.rim ?? p.obj.ring} />
          <stop offset="0.5" stop-color={p.obj.ring} />
          <stop offset="1"  stop-color={p.obj.rim ?? p.obj.ring} />
        </linearGradient>
      </defs>
      {/* Top collar — thicker cylinder where the objective threads into the nosepiece. */}
      <rect x="6" y="2" width="16" height="5" rx="1.4"
            fill={`url(#barrel-${p.obj.x})`} stroke="#0B1220" stroke-width="0.5" />
      {/* Main truncated-cone body. */}
      <path d="M 7 7 L 21 7 L 19 36 L 9 36 Z"
            fill={`url(#barrel-${p.obj.x})`} stroke="#0B1220" stroke-width="0.5" />
      {/* Colour band — the whole reason any pathologist knows the mag at a glance. */}
      <rect x="7.5" y="14" width="13" height="4.5"
            fill={`url(#ring-${p.obj.x})`} stroke="#0B1220" stroke-width="0.35" />
      {/* Lens-cap: dark ellipse at the bottom + a thin glint for glassiness. */}
      <ellipse cx="14" cy="37.5" rx="5" ry="1.4"
               fill="#020617" stroke="#0B1220" stroke-width="0.4" />
      <ellipse cx="14" cy="37" rx="3.2" ry="0.6"
               fill="#1E293B" opacity="0.9" />
      {/* Active halo — outer ring glows in brand green when this objective is selected. */}
      <Show when={p.active}>
        <rect x="5.2" y="1" width="17.6" height="40" rx="3"
              fill="none" stroke="#27A567" stroke-width="1.2" />
      </Show>
    </svg>
  );
}

/** Horizontal row of five objectives — the desktop layout. */
export function MagObjectiveRow(p: MagObjectivePickerProps) {
  const isActive = (x: number) => Math.abs(p.current - x) < x * 0.06;
  return (
    <div class="pointer-events-auto flex items-end gap-1 rounded-lg border border-ink-300 bg-ink-100/90 px-2 py-1.5 shadow-md backdrop-blur">
      <For each={PATHOLOGY_OBJECTIVES}>{(obj) => {
        const digital = obj.x > (p.nativeMag ?? 0) * 1.05;
        const active = () => isActive(obj.x);
        return (
          <button type="button"
                  class="group flex flex-col items-center rounded px-1 py-0.5 transition"
                  classList={{
                    "bg-brand/15 ring-1 ring-brand": active(),
                    "hover:bg-ink-200": !active(),
                  }}
                  aria-pressed={active()}
                  title={digital
                    ? `${obj.x}× objective-equivalent · digital zoom beyond native ${(p.nativeMag ?? 10).toFixed(0)}×`
                    : `${obj.x}× objective-equivalent`}
                  onClick={() => p.onPick(obj.x)}>
            <ObjectiveIcon obj={obj} active={active()} />
            <span class="mt-0.5 font-mono text-[10px] leading-none"
                  classList={{
                    "text-white": active(),
                    "text-ink-900": !active() && !digital,
                    "text-ink-700 italic": !active() && digital,
                  }}>
              {obj.x}×{digital ? "ᴰ" : ""}
            </span>
          </button>
        );
      }}</For>
    </div>
  );
}

/** Compact dropdown for mobile — a single pill showing the current
 *  objective; tap to open a popover with all five. */
export function MagObjectiveDropdown(p: MagObjectivePickerProps) {
  const [open, setOpen] = createSignal(false);
  const active = () => PATHOLOGY_OBJECTIVES.find((o) => Math.abs(p.current - o.x) < o.x * 0.06)
                     ?? PATHOLOGY_OBJECTIVES[1];   // default to 10× chip
  return (
    <div class="pointer-events-auto relative">
      <button type="button"
              class="flex items-center gap-2 rounded-full border border-ink-300 bg-ink-100/95 px-2.5 py-1 shadow-md backdrop-blur hover:bg-ink-200"
              aria-expanded={open()}
              title={`Change magnification (current ${p.current.toFixed(1)}×)`}
              onClick={() => setOpen((v) => !v)}>
        <ObjectiveIcon obj={active()} active={true} size={20} />
        <span class="font-mono text-[11px] text-white">
          {p.current > 0.01
            ? `${p.current.toFixed(p.current < 10 ? 1 : 0)}×${p.digital ? "ᴰ" : ""}`
            : "—"}
        </span>
        <span class="text-[8px] text-ink-700">▾</span>
      </button>
      <Show when={open()}>
        {/* Click-away shim */}
        <div class="fixed inset-0 z-40" onClick={() => setOpen(false)} />
        <div class="absolute left-1/2 z-50 mt-1.5 flex -translate-x-1/2 flex-col gap-1 rounded-lg border border-ink-300 bg-ink-100 px-2 py-2 shadow-panel">
          <For each={PATHOLOGY_OBJECTIVES}>{(obj) => {
            const digital = obj.x > (p.nativeMag ?? 0) * 1.05;
            const isA = Math.abs(p.current - obj.x) < obj.x * 0.06;
            return (
              <button type="button"
                      class="flex items-center gap-2 rounded px-2 py-1 transition"
                      classList={{
                        "bg-brand/20 ring-1 ring-brand": isA,
                        "hover:bg-ink-200": !isA,
                      }}
                      onClick={() => { p.onPick(obj.x); setOpen(false); }}>
                <ObjectiveIcon obj={obj} active={isA} size={22} />
                <span class="font-mono text-[11px]"
                      classList={{
                        "text-white": isA,
                        "text-ink-900": !isA && !digital,
                        "text-ink-700 italic": !isA && digital,
                      }}>
                  {obj.x}×{digital ? "ᴰ" : ""}
                </span>
              </button>
            );
          }}</For>
        </div>
      </Show>
    </div>
  );
}
